import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { actions } from 'features/generalReport/redux';
import { addNotify } from 'features/notify';

import { convertGeneralModel } from './convertGeneralModel';
import { periodItemsData } from '../data';

const useGeneralModel = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector(s => s.locale, shallowEqual);
  const { actionProcessing, statisticProvider } = useSelector(s => s.generalReport, shallowEqual);

  const periodItems = periodItemsData.map(v => ({
    ...v,
    renderElement: locale[v.renderElement],
    selectedRenderElement: locale[v.selectedRenderElement],
  }));

  const [state, setState] = useState({
    periodItems,
    search: '',
    activeDay: '',
    beginDate: dayjs().add(-1, 'week').toDate(),
    endDate: dayjs().toDate(),
    beginDatePrev: dayjs().add(-2, 'week').toDate(),
    endDatePrev: dayjs().add(-1, 'week').toDate(),
    sortBy: { isOpen: false, type: 'betCount', direction: 'desc' },
    withDiff: false,
  });

  const [triggerUpdate, setTriggerUpdate] = useState({});

  const validateDates = (checkState = state) => {
    const isDatesValid = !dayjs(checkState.beginDate).isAfter(dayjs(checkState.endDate), 'day');
    const isPrevDatesValid = checkState.withDiff
      ? !dayjs(checkState.beginDatePrev).isAfter(dayjs(checkState.endDatePrev), 'day')
      : true;

    const isValid = isDatesValid && isPrevDatesValid;

    if (!isValid) {
      dispatch(addNotify(locale.dateRangeError, 'error'));
    }

    return isValid;
  };

  const handleValueChange = (k, v) => {
    const withDateChange = ['beginDate', 'endDate'].includes(k);
    const withPrevDateChange = ['beginDatePrev', 'endDatePrev'].includes(k);

    const newState = {
      ...state,
      [k]: v,
      activeDay: withDateChange ? '' : state.activeDay,
    };

    if (withDateChange || withPrevDateChange) {
      const isValid = validateDates(newState);

      if (!isValid) {
        setState(ps => ({
          ...state,
          [k]: dayjs(ps[k]),
        }));

        return;
      }
    }

    setState(newState);
  };

  const handleDateChange = (k, v) => {
    const withDayChange = ['beginDate', 'endDate'].includes(k);

    const newState = {
      ...state,
      [k]: v,
      activeDay: withDayChange ? '' : state.activeDay,
    };

    const isValid = validateDates(newState);

    if (!isValid) {
      // to reupdate date in a datepicker
      setState(ps => ({
        ...state,
        [k]: dayjs(ps[k]),
      }));

      return;
    }

    setState(newState);
  };

  const handleActiveDayChange = dayFilter => {
    setState(ps => ({
      ...ps,
      activeDay: dayFilter,
      beginDate: dayFilter.value,
      endDate: dayFilter.value2,
    }));
  };

  const handleToggleWithDiff = withDiff => {
    setState(ps => ({
      ...ps,
      withDiff,
    }));

    setTriggerUpdate({});
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    setTriggerUpdate({});
  };

  const handleKeyDown = e => {
    if (e?.key === 'Enter') {
      e.preventDefault();
      setTriggerUpdate({});
    }
  };

  useEffect(() => {
    setState(s => ({
      ...s,
      periodItems,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (!actionProcessing) {
      const isValid = validateDates();

      if (isValid) {
        dispatch(actions.getStatisticProvider(convertGeneralModel(state)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerUpdate]);

  return {
    handlers: {
      handleValueChange,
      handleActiveDayChange,
      handleDateChange,
      handleToggleWithDiff,
      handleFormSubmit,
      handleKeyDown,
    },
    values: {
      state,
      statisticProvider,
      actionProcessing,
    },
  };
};

export default useGeneralModel;
